<template>
  <v-card class="mt-7 px-5 pt-1 och-packInfo--fixed" color="grey lighten-3">
    <v-row align="center">
      <v-col cols="12" sm="6" md="6">
        <div v-show="!showDropdown">
          <div :class="`primary--text text--darken-2`">
            <div>
              Pack:
              <span data-cy="packType" class="text-uppercase font-weight-bold">{{ pack.type }}</span> / Status:
              <span data-cy="packStatus" class="text-uppercase font-weight-bold">{{ pack.status }}</span>
            </div>
            <div v-if="!isOchinUser" class="pack-info__lead mt-1">
              <span>Pack Lead:</span>
              <span
                data-cy="pack-info-pack-lead"
                data-test="pack-info-pack-lead"
                class="text-uppercase font-weight-bold"
              >
                {{ pack.memberPackLeadDisplayName || "Unassigned" }}
              </span>
              <span
                v-if="pack.memberPackLeadDisplayName && pack.memberPackLeadEmail"
                data-cy="pack-info-pack-lead-email"
                data-test="pack-info-pack-lead-email"
                class="font-weight-regular text-lowercase"
              >
                ({{ pack.memberPackLeadEmail }})
              </span>
              <v-btn
                v-if="canEditMemberPack && !isOchinUser"
                icon
                x-small
                class="primary--text text--darken-2"
                @click="editPackLead"
                aria-label="Edit pack lead"
                data-cy="packInfo__button--editPackLead"
                data-test="pack-info-edit-pack-lead"
                title="Edit pack lead"
              >
                <v-icon small>fas fa-edit</v-icon>
              </v-btn>
            </div>
          </div>
          <span v-if="packListDataIsLoading"
            ><v-icon class="spinner mr-2" size="15">fa fa-spinner</v-icon><em>Pack data is loading...</em></span
          >
          <span data-cy="packTitle" v-html="pack.title" class="subtitle-1" v-else></span>

          <v-menu offset-y v-if="showMenu && !packListDataIsLoading">
            <template v-slot:activator="{ on }">
              <v-btn
                :absolute="false"
                @click="showDropdown = true"
                data-cy="packSelectorButton"
                dense
                icon
                slot="activator"
                small
                v-on="on"
              >
                <v-icon icon small text dense>fas fa-caret-down</v-icon>
              </v-btn>
            </template>
          </v-menu>
        </div>
        <div v-show="showDropdown" class="py-3">
          <pack-selector
            :loading="packListDataIsLoading"
            :packList="packList"
            @selectPack="selectPack"
          ></pack-selector></div
      ></v-col>

      <v-col cols="12" sm="6" md="6">
        <v-row justify="end" align-center>
          <div>
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  @click="navigateToDashboard()"
                  class="text--darken-2"
                  color="primary"
                  data-cy="packInfo__button--myDashboard"
                  small
                  text
                  v-on="tooltip"
                >
                  <i class="fas fa-chart-pie mr-1" aria-hidden="true"></i>My Dashboard
                </v-btn>
              </template>
              <span>View Dashboard for {{ pack.title }}</span>
            </v-tooltip>
          </div>
          <div v-if="hasReleaseNoteCreateEdit">
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  @click="createReleaseNote"
                  class="text--darken-2"
                  color="primary"
                  data-cy="releaseNoteCreateButton"
                  small
                  text
                  v-on="tooltip"
                >
                  <i class="fas fa-plus mr-1" aria-hidden="true"></i>Create Release Note
                </v-btn>
              </template>
              <span>Create a Release Note for {{ pack.title }}</span>
            </v-tooltip>
          </div>
          <div v-if="hasReleaseNoteCreateEdit && isOchinUser">
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  @click="$router.push({ path: '/jira-import' })"
                  class="text--darken-2"
                  color="primary"
                  data-cy="packInfo__button--qaJiraImport"
                  small
                  text
                  v-on="tooltip"
                >
                  <i class="fas fa-file-import mr-1" aria-hidden="true"></i>QA Import
                </v-btn>
              </template>
              <span>Import a QA Jira Issue as a Release Note</span>
            </v-tooltip>
          </div>
          <div>
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  @click="viewReleaseNotes"
                  class="text--darken-2"
                  color="primary"
                  data-cy="packInfo__button--releaseNotes"
                  small
                  text
                  v-on="tooltip"
                >
                  <i class="fas fa-list mr-1" aria-hidden="true"></i>Release Notes
                </v-btn>
              </template>
              <span>View all Release Notes for {{ pack.title }}</span>
            </v-tooltip>
          </div>
          <div v-if="hasAdminOchin">
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  @click="navigateToPackSummary"
                  class="text--darken-2"
                  color="primary"
                  data-cy="packSummaryButton"
                  small
                  text
                  v-on="tooltip"
                >
                  <i class="fas fa-archive mr-1" aria-hidden="true"></i>Pack Summary
                </v-btn>
              </template>
              <span>View the Pack Summary for {{ pack.title }}</span>
            </v-tooltip>
          </div>
        </v-row>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import PackSelector from "@/components/PackSelector";
import { isNavigationFailure, NavigationFailureType } from "vue-router";

export default {
  name: "packInfo",
  components: { PackSelector },
  computed: {
    isOchinUser() {
      return this.user?.isOchinUser;
    },
    packId() {
      return parseInt(this.$route.params.packId, 10);
    },
    ...mapGetters({
      hasAdminOchin: "session/hasAdminOchin",
      hasMemberPackEdit: "session/hasMemberPackEdit",
      hasReleaseNoteCreateEdit: "session/hasReleaseNoteCreateEdit",
    }),
    ...mapState({
      pack: state => state.pack.pack,
      packList: state => state.pack.packList,
      releaseNoteListPage: state => state.releaseNote.releaseNoteListPage,
      user: state => state.session.user,
    }),
    canEditMemberPack() {
      return this.hasMemberPackEdit;
    },
  },
  async created() {
    this.packListDataIsLoading = true;

    if (!this.packList.length) {
      try {
        await this.fetchPackList();
      } catch (e) {
        console.error(e);
        this.$notifyError({ message: "Could not fetch pack list" });
      }
    }

    this.packListDataIsLoading = false;
    this.selectedPackId = this.packId;

    try {
      this.getPack(this.packId);
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: "Could not fetch pack" });
    }
  },
  data() {
    return {
      packListDataIsLoading: false,
      selectedPackId: null,
      showDropdown: false,
    };
  },
  methods: {
    navigateToDashboard() {
      this.$router.push({ name: "dashboard", params: { packId: this.packId } }).catch((error) => {
        // Duplicated: double-click, etc.
        // Cancelled: superseded by a subsequent navigation — navigation still completed.
        // Redirected: the dashboard beforeEnter redirects users to their specific dashboard type
        //             (e.g. uatDashboard) — navigation still completed.
        if (isNavigationFailure(error, NavigationFailureType.duplicated)) return;
        if (isNavigationFailure(error, NavigationFailureType.cancelled)) return;
        if (isNavigationFailure(error, NavigationFailureType.redirected)) return;
        console.error(error);
        this.$notifyError({ message: "Could not navigate to dashboard. Please try again." });
      });
    },
    createReleaseNote() {
      this.$router.push({
        name: "releaseNoteCreate",
        params: { packId: this.packId },
      });
    },
    async getPack(id) {
      try {
        await this.fetchPack({ id });
      } catch (error) {
        console.error(error);
        this.$notifyError({ message: "Could not fetch pack" });
      }
    },
    navigateToPackSummary() {
      this.$router.push({
        name: "adminPackView",
        params: { id: this.packId },
      });
    },
    reloadPage(packId) {
      const params = Object.assign({}, this.$route.params, { packId });
      this.$router.push({ params });
      this.getPack(packId);
      this.showDropdown = false;
    },
    selectPack(packId) {
      this.selectedPackId = packId;
    },
    viewReleaseNotes() {
      let name = "testPlanList";

      if (this.user.isOchinUser) {
        name = "releaseNoteList";
      }

      this.$router.push({
        name,
        params: {
          packId: this.packId,
        },
        query: {
          page: this.releaseNoteListPage,
        },
      }).catch(err => {
        if (err.name !== "NavigationDuplicated") throw err;
      });
    },
    ...mapActions({
      fetchPack: "pack/getPack",
      fetchPackList: "pack/getList",
    }),
    editPackLead() {
      // Prevent navigation if already on the edit page
      if (
        this.$route.name === "memberPackEdit" &&
        this.$route.params.packId === String(this.packId) &&
        this.$route.params.serviceAreaId === String(this.user.serviceArea)
      ) {
        return;
      }

      const params = { packId: this.packId, serviceAreaId: this.user.serviceArea };
      this.$router.push({ name: "memberPackEdit", params });
    },
  },
  props: { showMenu: { type: Boolean, default: false } },
  watch: {
    selectedPackId(val) {
      if (this.packId !== val) {
        this.reloadPage(val);
      }
    },
    packId(val) {
      this.getPack(val);
    },
  },
};
</script>
<style>
.och-packInfo--fixed {
  position: fixed !important;
  width: 100%;
  z-index: 5;
}

.pack-info__lead {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.pack-info__lead > span {
  white-space: nowrap;
}
</style>
